import { zodResolver } from '@hookform/resolvers/zod';
import type { LeadFormFieldDto } from '@up2stay/up2promo-api-client';
import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';
import { Permission } from '@up2stay/up2promo-api-client';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
  useForm,
  useWatch,
} from 'react-hook-form';
import { toast } from 'sonner';

import {
  areProfileFormValuesEqual,
  areProfileModulesEqual,
  buildFixedLeadFormFields,
  PROFILE_EDIT_DEFAULT_VALUES,
  type ProfileFormData,
  profileFormSchema,
} from '@/core/components/profile/edit/profile-edit-form-schema';
import { buildProfileEditUpdatePayload } from '@/core/components/profile/edit/build-profile-edit-update-payload';
import {
  buildProfileStylingPayload,
  PROFILE_STYLING_FORM_DEFAULTS,
} from '@/core/lib/profile-styling-form';
import { useAutosaveCue } from '@/core/hooks/use-autosave-cue';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useLocations } from '@/core/hooks/use-locations';
import { usePermissions } from '@/core/hooks/use-permissions';
import { type UpdateProfileInput, useProfile } from '@/core/hooks/use-profile';
import { useProfileTemplate } from '@/core/hooks/use-profile-template';
import { useTeamAssignedProfileTemplate } from '@/core/hooks/use-team-assigned-profile-template';
import { useTeamMembersDisplay } from '@/core/hooks/use-team-members-display';
import { useUserTeamMembership } from '@/core/hooks/use-user-team-membership';
import { LIST_PAGE_HARD_CAP } from '@/core/lib/list-pagination';
import {
  isModuleContentCustomized,
  resetModuleContentToTemplate,
} from '@/core/lib/profile-module-entry-policies';
import {
  buildPreviewModules,
  omitTeamMembersModules,
} from '@/core/lib/profile-preview';
import { parseTemplateContent } from '@/core/lib/profile-template-content';
import {
  isFieldLocked,
  type ProfileTemplateFieldKey,
} from '@/core/lib/profile-template-fields';
import {
  contentToFormValues,
  mergeProfileEditFromTemplate,
  profileToEditFormValues,
  type TemplateFormData,
} from '@/core/lib/profile-template-form';
import {
  applyTemplateFormFieldReset,
  canShowFormFieldOverrideHint,
  getTemplateFormFieldDisplay,
  isFormFieldCustomized,
  type TemplateFormFieldDisplayLabels,
} from '@/core/lib/profile-template-overrides';
import {
  getLatestTemplateSyncStartedEvent,
  subscribeTemplateSyncStarted,
} from '@/core/lib/profile-template-sync-events';
import { getProfileTemplateSyncStatus } from '@/core/lib/profile-template-sync-status';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useAuthStore } from '@/core/store/auth.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export type ProfileEditPreviewData = {
  id?: string;
  title: string;
  jobTitle?: string;
  company?: string;
  location: string | null;
  profileImage: { path: string } | null;
  showProfileImage: boolean;
  showUp2promoBranding: boolean;
  styling: ReturnType<typeof buildProfileStylingPayload>;
  ownerPhoto: { path: string } | null;
  workspacePhoto: { path: string } | null;
  modules: ReturnType<typeof buildPreviewModules>;
  leadForm: {
    enabled: boolean;
    visibilityMode: 'off' | 'manual' | 'auto_open';
    fields: LeadFormFieldDto[];
  };
};

export function useProfileEdit(options: {
  replaceWith: (path: string) => void;
}) {
  const { replaceWith } = options;
  const dict = useDictionary();
  const {
    profile,
    isLoading,
    error: profileLoadError,
    updateProfile,
    refetch,
  } = useProfile();
  const user = useAuthStore((state) => state.user);
  const currentWorkspace = useCurrentWorkspace();
  // DEV-15 / DEV-129: gate `team_members` on the viewer's own team membership.
  const { hasTeamMembership, isLoading: isTeamMembershipLoading } =
    useUserTeamMembership(currentWorkspace?.id ?? null);
  const { can } = usePermissions();
  const { isFeatureEnabled, isLoading: entitlementsLoading } =
    useEntitlements();
  const canCustomizeAccent = isFeatureEnabled('feature.profile_branding');
  // DEV-507: hiding the up2promo branding shares the accent's entitlement.
  const canHideBranding = isFeatureEnabled('feature.profile_branding');
  const canManageWorkspace = can(Permission.TEAMS_MANAGE);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const {
    status: autosaveStatus,
    markSaving,
    markSaved,
    markIdle,
  } = useAutosaveCue();
  const [activeTab, setActiveTab] = useState('basic');
  const [modules, setModules] = useState<ProfileModuleDto[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const { teamTemplateId, isLoading: isTeamTemplateLoading } =
    useTeamAssignedProfileTemplate(currentWorkspace?.id);
  const governedTemplateId = teamTemplateId ?? null;
  const {
    template,
    fieldPolicies,
    templateModules,
    isLoading: isTemplateLoading,
    refetch: refetchTemplate,
  } = useProfileTemplate(governedTemplateId);
  // A stale template query must not keep team modules after membership is gone.
  const templateStillApplies = isTeamMembershipLoading || hasTeamMembership;
  const templateGoverned = Boolean(governedTemplateId) && templateStillApplies;
  const modulesForSurface = useMemo(
    () => (templateStillApplies ? modules : omitTeamMembersModules(modules)),
    [modules, templateStillApplies],
  );
  const syncStatus = getProfileTemplateSyncStatus(profile, template);
  const [forceSyncPending, setForceSyncPending] = useState(false);
  const formSyncBlocked =
    syncStatus === 'pending' ||
    (forceSyncPending &&
      syncStatus !== 'synced' &&
      templateGoverned &&
      Boolean(currentWorkspace?.id) &&
      Boolean(governedTemplateId));
  const handledSyncEventTsRef = useRef(0);
  const refetchProfileRef = useRef(refetch);
  const refetchTemplateRef = useRef(refetchTemplate);
  const [previousSyncStatus, setPreviousSyncStatus] = useState(syncStatus);
  let completedTemplateSync = false;
  if (previousSyncStatus !== syncStatus) {
    completedTemplateSync =
      previousSyncStatus === 'pending' && syncStatus === 'synced';
    setPreviousSyncStatus(syncStatus);
  }
  const isLocked = (key: ProfileTemplateFieldKey) =>
    formSyncBlocked || isFieldLocked(fieldPolicies, key);
  const templateName = template?.name;
  const fieldLockedHintMessage = useMemo(() => {
    if (formSyncBlocked) {
      return dict.profiles.edit.templateBanner.syncPending;
    }
    if (templateName) {
      return dict.profiles.edit.fieldLockedHint.replace('{name}', templateName);
    }
    return dict.profiles.edit.fieldLockedHintGeneric;
  }, [
    dict.profiles.edit.fieldLockedHint,
    dict.profiles.edit.fieldLockedHintGeneric,
    dict.profiles.edit.templateBanner.syncPending,
    formSyncBlocked,
    templateName,
  ]);

  const templateFormValues = useMemo((): TemplateFormData | null => {
    if (!template?.content) return null;
    return contentToFormValues(parseTemplateContent(template.content));
  }, [template]);

  const templateOverrideLabels = dict.profiles.edit.templateOverride;

  const overrideHintMessage = useMemo(() => {
    if (templateName) {
      return templateOverrideLabels.hintWithName.replace(
        '{name}',
        templateName,
      );
    }
    return templateOverrideLabels.hint;
  }, [templateName, templateOverrideLabels]);

  const isModuleCustomized = useCallback(
    (moduleId: string) => {
      if (!templateGoverned || !templateModules.length) return false;
      const templateModule = templateModules.find(
        (module) => String(module.id ?? '') === moduleId,
      );
      const currentModule = modules.find(
        (module) => String(module.id ?? '') === moduleId,
      );
      if (!templateModule || !currentModule) return false;
      return isModuleContentCustomized(currentModule, templateModule);
    },
    [modules, templateGoverned, templateModules],
  );

  const handleResetModuleToTemplate = useCallback(
    (moduleId: string) => {
      setModules((current) =>
        resetModuleContentToTemplate(current, templateModules, moduleId),
      );
    },
    [templateModules],
  );

  useEffect(() => {
    refetchProfileRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    refetchTemplateRef.current = refetchTemplate;
  }, [refetchTemplate]);

  useEffect(() => {
    if (!templateGoverned || isTemplateLoading) {
      return;
    }
    const intervalMs = syncStatus === 'pending' ? 3000 : 12000;
    const interval = window.setInterval(() => {
      void refetch();
      void refetchTemplate();
    }, intervalMs);
    return () => window.clearInterval(interval);
  }, [
    isTemplateLoading,
    refetch,
    refetchTemplate,
    syncStatus,
    templateGoverned,
  ]);

  useEffect(() => {
    if (!templateGoverned || !currentWorkspace?.id || !governedTemplateId) {
      return;
    }

    const maybeStartPending = (event: {
      workspaceId: string;
      templateId: string;
      timestamp: number;
    }) => {
      if (event.workspaceId !== currentWorkspace.id) return;
      if (event.templateId !== governedTemplateId) return;
      // Ignore stale events (e.g. persisted from previous sessions).
      if (Date.now() - event.timestamp > 3 * 60 * 1000) return;
      if (event.timestamp <= handledSyncEventTsRef.current) return;
      handledSyncEventTsRef.current = event.timestamp;
      setForceSyncPending((prev) => (prev ? prev : true));
      void refetchProfileRef.current();
      void refetchTemplateRef.current();
    };

    const latestEvent = getLatestTemplateSyncStartedEvent();
    if (latestEvent) {
      maybeStartPending(latestEvent);
    }

    const unsubscribe = subscribeTemplateSyncStarted((event) => {
      maybeStartPending(event);
    });

    return unsubscribe;
  }, [currentWorkspace?.id, governedTemplateId, templateGoverned]);

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncedProfileIdRef = useRef<string | null>(null);
  const previousFormValuesRef = useRef<ProfileFormData | null>(null);
  const previousModulesRef = useRef<ProfileModuleDto[] | null>(null);
  /** Disarmed until live form matches baseline after hydrate (blocks enter flash). */
  const autosaveArmedRef = useRef(false);
  const formValuesRef = useRef<ProfileFormData | null>(null);
  const modulesRef = useRef<ProfileModuleDto[] | null>(null);

  const fixedLeadFormFields: LeadFormFieldDto[] = useMemo(() => {
    const leadFormDict =
      (
        dict?.profiles?.edit as {
          leadForm?: { fieldLabels?: Record<string, string> };
        }
      )?.leadForm ?? undefined;
    return buildFixedLeadFormFields(leadFormDict?.fieldLabels);
  }, [dict]);

  const missingProfileRedirect =
    Boolean(currentWorkspace?.id) &&
    !isLoading &&
    profile == null &&
    profileLoadError == null;

  useLayoutEffect(() => {
    if (missingProfileRedirect) {
      replaceWith(shellPaths.profiles);
    }
  }, [missingProfileRedirect, replaceWith]);

  const {
    register,
    control,
    formState: { errors },
    setValue,
    reset,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema as never),
    defaultValues: PROFILE_EDIT_DEFAULT_VALUES,
  });

  // useWatch (not watch()) — React Compiler compatible
  const formValues = useWatch({ control }) as ProfileFormData;
  useEffect(() => {
    formValuesRef.current = formValues;
    modulesRef.current = modulesForSurface;
  }, [formValues, modulesForSurface]);
  const resolvedAccentColor =
    entitlementsLoading || canCustomizeAccent
      ? (formValues.accentColor ?? null)
      : null;
  const resolvedShowUp2promoBranding =
    entitlementsLoading || canHideBranding
      ? (formValues.showUp2promoBranding ?? true)
      : true;
  const currentTemplateFormValues = useMemo<TemplateFormData>(
    () => ({
      jobTitle: formValues.jobTitle ?? null,
      company: formValues.company ?? null,
      location: formValues.location ?? null,
      showProfileImage: formValues.showProfileImage ?? true,
      language: formValues.language ?? 'auto',
      visibility: formValues.visibility ?? 'unlisted',
      leadFormEnabled: formValues.leadFormEnabled ?? false,
      leadFormVisibilityMode: formValues.leadFormVisibilityMode ?? 'off',
      leadFormAutoOpenDelay:
        typeof formValues.leadFormAutoOpenDelay === 'number'
          ? formValues.leadFormAutoOpenDelay
          : (formValues.leadFormAutoOpenDelay ?? 0),
      leadsAutoShareWithWorkspace:
        formValues.leadsAutoShareWithWorkspace ?? false,
      showUp2promoBranding: formValues.showUp2promoBranding ?? true,
      accentColor: formValues.accentColor ?? null,
      layout: formValues.layout ?? PROFILE_STYLING_FORM_DEFAULTS.layout,
      theme: formValues.theme ?? PROFILE_STYLING_FORM_DEFAULTS.theme,
      headerStyle:
        formValues.headerStyle ?? PROFILE_STYLING_FORM_DEFAULTS.headerStyle,
      headerImage: formValues.headerImage ?? null,
      buttonShape: formValues.buttonShape ?? null,
    }),
    [
      formValues.jobTitle,
      formValues.company,
      formValues.location,
      formValues.showProfileImage,
      formValues.language,
      formValues.visibility,
      formValues.leadFormEnabled,
      formValues.leadFormVisibilityMode,
      formValues.leadFormAutoOpenDelay,
      formValues.leadsAutoShareWithWorkspace,
      formValues.showUp2promoBranding,
      formValues.accentColor,
      formValues.layout,
      formValues.theme,
      formValues.headerStyle,
      formValues.headerImage,
      formValues.buttonShape,
    ],
  );

  const isFieldCustomized = useCallback(
    (key: ProfileTemplateFieldKey) => {
      if (!templateFormValues) return false;
      return isFormFieldCustomized(
        key,
        currentTemplateFormValues,
        templateFormValues,
      );
    },
    [currentTemplateFormValues, templateFormValues],
  );

  const resetFieldToTemplate = useCallback(
    (key: ProfileTemplateFieldKey) => {
      if (!templateFormValues) return;
      applyTemplateFormFieldReset(key, templateFormValues, setValue as never);
    },
    [setValue, templateFormValues],
  );

  const formFieldDisplayLabels = useMemo((): TemplateFormFieldDisplayLabels => {
    const leadVisibility = dict.profiles.edit.leadForm.visibilityMode;
    return {
      emptyValue: templateOverrideLabels.emptyValue,
      yes: templateOverrideLabels.yes,
      no: templateOverrideLabels.no,
      language: {
        auto: dict.profiles.edit.settings.languageAuto,
        de: dict.profiles.edit.settings.languageGerman,
        en: dict.profiles.edit.settings.languageEnglish,
      },
      visibility: {
        unlisted: dict.profiles.edit.settings.visibilityUnlisted,
        private: dict.profiles.edit.settings.visibilityPrivate,
      },
      leadFormEnabled: templateOverrideLabels.leadFormEnabled,
      leadFormVisibility: templateOverrideLabels.leadFormVisibility,
      leadFormDelay: templateOverrideLabels.leadFormDelay,
      leadFormDelaySeconds: templateOverrideLabels.leadFormDelaySeconds,
      leadFormVisibilityMode: {
        off: leadVisibility.off,
        manual: leadVisibility.manual,
        autoOpen: leadVisibility.autoOpen,
      },
      leadsAutoShare: dict.profiles.edit.leadsWorkspace.toggleLabel,
    };
  }, [dict.profiles.edit, templateOverrideLabels]);

  const getFieldOverrideDisplay = useCallback(
    (key: ProfileTemplateFieldKey) => {
      const customized =
        canShowFormFieldOverrideHint(
          key,
          fieldPolicies,
          templateGoverned,
          formSyncBlocked,
        ) && isFieldCustomized(key);
      const display =
        customized && templateFormValues
          ? getTemplateFormFieldDisplay(
              key,
              currentTemplateFormValues,
              templateFormValues,
              formFieldDisplayLabels,
            )
          : null;

      return {
        customized,
        templateValue: display?.single,
        templateDetails: display?.details,
      };
    },
    [
      currentTemplateFormValues,
      fieldPolicies,
      formFieldDisplayLabels,
      formSyncBlocked,
      isFieldCustomized,
      templateFormValues,
      templateGoverned,
    ],
  );

  // Lead form: reset autoOpenDelay when visibility mode is not auto_open
  useEffect(() => {
    const mode = formValues.leadFormVisibilityMode;
    const delay = formValues.leadFormAutoOpenDelay;
    if (mode !== 'auto_open' && delay != null && delay !== 0) {
      setValue('leadFormAutoOpenDelay', 0, { shouldDirty: false });
    }
  }, [
    formValues.leadFormVisibilityMode,
    formValues.leadFormAutoOpenDelay,
    setValue,
  ]);

  // Sync profile → form when the profile identity changes (e.g. after switching workspace).
  // Also run after save: we avoid overwriting by updating cache with setQueryData, so profile id stays the same and we don't re-run unnecessarily.
  useEffect(() => {
    if (templateGoverned && isTemplateLoading) {
      return;
    }
    if (!profile) {
      lastSyncedProfileIdRef.current = null;
      autosaveArmedRef.current = false;
      return;
    }
    const profileSyncKey = `${profile.id}:${governedTemplateId ?? ''}:${profile.profileTemplateSyncedAt ?? ''}:${profile.profileTemplateSyncedRevision ?? ''}:${template?.updatedAt ?? ''}`;
    if (lastSyncedProfileIdRef.current === profileSyncKey) return;

    const currentFormValues = formValuesRef.current;
    const currentModules = modulesRef.current;
    const hasLocalEdits =
      previousFormValuesRef.current != null &&
      currentFormValues != null &&
      currentModules != null &&
      (!areProfileFormValuesEqual(
        currentFormValues,
        previousFormValuesRef.current,
      ) ||
        !areProfileModulesEqual(currentModules, previousModulesRef.current));

    // Background refetch must not clobber local edits, except when a template sync
    // just completed and we need to pull the freshly synchronized server state.
    if (
      hasLocalEdits &&
      lastSyncedProfileIdRef.current != null &&
      !completedTemplateSync
    ) {
      lastSyncedProfileIdRef.current = profileSyncKey;
      return;
    }

    lastSyncedProfileIdRef.current = profileSyncKey;
    autosaveArmedRef.current = false;

    let initialFormValues = profileToEditFormValues(profile) as ProfileFormData;
    let initialModules = (profile.modules ??
      []) as unknown as ProfileModuleDto[];

    if (
      templateStillApplies &&
      template &&
      template.id === governedTemplateId
    ) {
      const merged = mergeProfileEditFromTemplate({
        profile,
        templateContent: parseTemplateContent(template.content),
        fieldPolicies,
        templateGoverned,
      });
      initialFormValues = merged.form as ProfileFormData;
      initialModules = merged.modules;
    }

    if (!isTeamMembershipLoading && !hasTeamMembership) {
      initialModules = omitTeamMembersModules(initialModules);
    }

    if (
      initialFormValues.leadFormVisibilityMode !== 'auto_open' &&
      initialFormValues.leadFormAutoOpenDelay != null &&
      initialFormValues.leadFormAutoOpenDelay !== 0
    ) {
      initialFormValues = {
        ...initialFormValues,
        leadFormAutoOpenDelay: 0,
      };
    }

    reset(initialFormValues);
    setModules(initialModules);

    previousFormValuesRef.current = initialFormValues;
    previousModulesRef.current = initialModules;
  }, [
    profile,
    template,
    isTemplateLoading,
    fieldPolicies,
    governedTemplateId,
    templateGoverned,
    templateStillApplies,
    hasTeamMembership,
    isTeamMembershipLoading,
    completedTemplateSync,
    reset,
  ]);

  // Auto-save handler with debounce
  const handleSave = useCallback(async () => {
    if (!profile) {
      return;
    }

    if (formSyncBlocked) {
      return;
    }

    if (entitlementsLoading) {
      return;
    }

    if (isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    setSaveError(null);
    markSaving();

    // Generate title from user's first and last name
    const userTitle = user
      ? `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
        user.email ||
        'User'
      : 'User';

    // Snapshot what we attempted so a failed save does not re-fire forever
    // (handleSave was previously in the effect deps via isSaving, which
    // retriggered autosave while previous* refs still looked "dirty").
    const attemptedFormValues = { ...formValues };
    const attemptedModules = [...modulesForSurface];

    try {
      const updateData = buildProfileEditUpdatePayload({
        userTitle,
        formValues: attemptedFormValues,
        modules: attemptedModules,
        fixedLeadFormFields,
        resolvedAccentColor,
        resolvedShowUp2promoBranding,
      });

      await updateProfile(updateData);

      // Update refs after successful save
      previousFormValuesRef.current = attemptedFormValues;
      previousModulesRef.current = attemptedModules;
      markSaved();
    } catch (err) {
      // Treat this attempt as acknowledged so the autosave effect does not
      // hammer the API with the same failing payload.
      previousFormValuesRef.current = attemptedFormValues;
      previousModulesRef.current = attemptedModules;
      markIdle();
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to save profile';
      setSaveError(errorMessage);
      toast.error(errorMessage);
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [
    profile,
    user,
    formValues,
    modulesForSurface,
    fixedLeadFormFields,
    updateProfile,
    formSyncBlocked,
    entitlementsLoading,
    resolvedAccentColor,
    resolvedShowUp2promoBranding,
    markSaving,
    markSaved,
    markIdle,
  ]);

  // Auto-save with debounce when form values or modules change
  const handleSaveRef = useRef(handleSave);
  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  useEffect(() => {
    if (formSyncBlocked) {
      return;
    }

    // Skip until we have synced a profile (avoids save before profile → form sync)
    if (lastSyncedProfileIdRef.current == null) {
      return;
    }

    const formValuesChanged = !areProfileFormValuesEqual(
      formValues,
      previousFormValuesRef.current,
    );
    const modulesChanged = !areProfileModulesEqual(
      modulesForSurface,
      previousModulesRef.current,
    );

    // Arm only after live state matches the hydrate baseline — prevents the
    // enter flash when useWatch still holds defaults while previous* is set.
    if (!autosaveArmedRef.current) {
      if (!formValuesChanged && !modulesChanged) {
        autosaveArmedRef.current = true;
      }
      return;
    }

    if (!formValuesChanged && !modulesChanged) {
      return;
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for debounced save (250ms)
    saveTimeoutRef.current = setTimeout(() => {
      void handleSaveRef.current();
    }, 250);

    // Cleanup on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formSyncBlocked, formValues, modulesForSurface]);

  // Must be before any conditional return (rules of hooks)
  const { members: resolvedTeamMembers } = useTeamMembersDisplay(
    currentWorkspace?.id ?? null,
    {
      enabled: Boolean(currentWorkspace?.id) && hasTeamMembership,
      workspaceUsername: currentWorkspace?.username ?? null,
    },
  );

  const { locations } = useLocations(undefined, {
    limit: LIST_PAGE_HARD_CAP,
    queryKeySuffix: 'profile-edit-preview',
  });
  const locationsById = useMemo(() => {
    const map = new Map<
      string,
      {
        name?: string | null;
        address?: string | null;
        coordinates?: {
          latitude?: number | null;
          longitude?: number | null;
        } | null;
      }
    >();
    for (const location of locations) {
      map.set(location.id, location);
    }
    return map;
  }, [locations]);

  const previewModules = useMemo(
    () =>
      buildPreviewModules({
        modules: modulesForSurface,
        selfUserId: user?.id ?? null,
        selfUsername: user?.username ?? null,
        teamMembersFallback: resolvedTeamMembers,
        locationsById,
      }),
    [
      modulesForSurface,
      resolvedTeamMembers,
      user?.id,
      user?.username,
      locationsById,
    ],
  );

  const previewProfileData = useMemo(
    (): ProfileEditPreviewData => ({
      id: profile?.id,
      title: user
        ? `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
          user.email ||
          'User'
        : 'User',
      jobTitle: formValues.jobTitle || undefined,
      company: formValues.company || undefined,
      location: formValues.location || null,
      profileImage: profile?.profileImage || null,
      showProfileImage: formValues.showProfileImage ?? true,
      showUp2promoBranding: resolvedShowUp2promoBranding,
      styling: buildProfileStylingPayload(
        {
          layout: formValues.layout ?? PROFILE_STYLING_FORM_DEFAULTS.layout,
          theme: formValues.theme ?? PROFILE_STYLING_FORM_DEFAULTS.theme,
          headerStyle:
            formValues.headerStyle ?? PROFILE_STYLING_FORM_DEFAULTS.headerStyle,
          headerImage: formValues.headerImage ?? null,
          buttonShape: formValues.buttonShape ?? null,
          accentColor: resolvedAccentColor,
        },
        { keepEphemeralPaths: true },
      ),
      ownerPhoto: user?.photo?.path ? { path: user.photo.path } : null,
      workspacePhoto:
        profile?.workspace && currentWorkspace?.photo?.path
          ? { path: currentWorkspace.photo.path }
          : null,
      modules: previewModules,
      leadForm: {
        enabled: formValues.leadFormEnabled ?? false,
        visibilityMode: formValues.leadFormVisibilityMode ?? 'off',
        fields: fixedLeadFormFields,
      },
    }),
    [
      profile?.id,
      profile?.profileImage,
      profile?.workspace,
      user,
      currentWorkspace,
      formValues.jobTitle,
      formValues.company,
      formValues.location,
      formValues.showProfileImage,
      resolvedShowUp2promoBranding,
      formValues.layout,
      formValues.theme,
      formValues.headerStyle,
      formValues.headerImage,
      formValues.buttonShape,
      resolvedAccentColor,
      formValues.leadFormEnabled,
      formValues.leadFormVisibilityMode,
      fixedLeadFormFields,
      previewModules,
    ],
  );

  const waitingForTemplateResolution =
    Boolean(currentWorkspace?.id) && isTeamTemplateLoading;
  const waitingForGovernedTemplate = templateGoverned && isTemplateLoading;
  const showLoading =
    isLoading ||
    waitingForTemplateResolution ||
    waitingForGovernedTemplate ||
    missingProfileRedirect;

  return {
    dict,
    profile,
    profileLoadError,
    refetch,
    user,
    currentWorkspace,
    hasTeamMembership,
    canManageWorkspace,
    canCustomizeAccent,
    canHideBranding,
    entitlementsLoading,
    saveError,
    autosaveStatus,
    activeTab,
    setActiveTab,
    modules: modulesForSurface,
    setModules,
    showPreview,
    setShowPreview,
    governedTemplateId,
    templateModules,
    templateGoverned,
    syncStatus,
    formSyncBlocked,
    isLocked,
    fieldLockedHintMessage,
    templateOverrideLabels,
    overrideHintMessage,
    isModuleCustomized,
    handleResetModuleToTemplate,
    register: register as UseFormRegister<ProfileFormData>,
    setValue: setValue as UseFormSetValue<ProfileFormData>,
    errors: errors as FieldErrors<ProfileFormData>,
    formValues,
    resolvedAccentColor,
    resolvedShowUp2promoBranding,
    getFieldOverrideDisplay,
    resetFieldToTemplate,
    previewProfileData,
    showLoading,
  };
}
